/**
 * nutrima storefront behaviours.
 *
 * Three small, dependency-free custom elements used by the nutrima sections:
 *   <nutrima-slider>   scroll-snap carousel with arrows, dots and optional autoplay
 *   <nutrima-tabs>     WAI-ARIA tabs with roving focus
 *   <nutrima-compare>  two-layer comparison driven by a native range input
 *
 * Everything degrades to usable markup without JavaScript: sliders scroll
 * natively, every tab panel is visible, and the comparison shows both layers.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class NutrimaSlider extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #index = 0;
  #paused = false;
  #abort = new AbortController();

  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-slider-track]'));
    if (!this.track) return;

    this.slides = /** @type {HTMLElement[]} */ (Array.from(this.track.querySelectorAll('[data-slider-slide]')));
    this.prevButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-slider-prev]'));
    this.nextButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-slider-next]'));
    this.dots = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-slider-dot]')));
    this.toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-slider-toggle]'));
    this.loop = this.hasAttribute('loop');

    const { signal } = this.#abort;

    this.setAttribute('data-ready', '');
    this.prevButton?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.nextButton?.addEventListener('click', () => this.go(this.#index + 1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i), { signal }));

    this.track.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') this.go(this.#index + (rtl ? -1 : 1));
        else if (event.key === 'ArrowLeft') this.go(this.#index + (rtl ? 1 : -1));
        else return;
        event.preventDefault();
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = this.slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (i > -1) this.#setActive(i);
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.#observer?.observe(slide));

    // Theme editor: bring the selected block's slide into view.
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const slide = /** @type {HTMLElement} */ (event.target).closest('[data-slider-slide]');
        const i = slide ? this.slides.indexOf(/** @type {HTMLElement} */ (slide)) : -1;
        if (i > -1) {
          this.pause();
          this.go(i);
        }
      },
      { signal }
    );

    if (this.hasAttribute('autoplay') && this.slides.length > 1) {
      this.toggle?.addEventListener('click', () => (this.#paused ? this.play() : this.pause()), { signal });
      for (const type of ['pointerenter', 'focusin']) this.addEventListener(type, () => this.#stop(), { signal });
      for (const type of ['pointerleave', 'focusout']) this.addEventListener(type, () => this.#start(), { signal });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : this.#start()), { signal });
      if (reducedMotion.matches) this.pause();
      else this.#start();
    } else if (this.toggle) {
      this.toggle.hidden = true;
    }

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  /** @param {number} target */
  go(target) {
    if (!this.track || !this.slides?.length) return;
    const count = this.slides.length;
    const index = this.loop ? (target + count) % count : Math.max(0, Math.min(count - 1, target));
    const slide = this.slides[index];
    if (!slide) return;

    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const rtl = getComputedStyle(this).direction === 'rtl';
    const delta = rtl ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;

    this.track.scrollBy({ left: delta, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  play() {
    this.#paused = false;
    this.toggle?.setAttribute('aria-pressed', 'false');
    this.removeAttribute('data-paused');
    this.#start();
  }

  pause() {
    this.#paused = true;
    this.toggle?.setAttribute('aria-pressed', 'true');
    this.setAttribute('data-paused', '');
    this.#stop();
  }

  #start() {
    if (this.#paused || !this.hasAttribute('autoplay')) return;
    this.#stop();
    const seconds = Number(this.getAttribute('autoplay')) || 6;
    this.#timer = window.setInterval(() => this.go((this.#index + 1) % this.slides.length), seconds * 1000);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /** @param {number} index */
  #setActive(index) {
    this.#index = index;
    this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === index));
    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (!this.loop) {
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }
    const counter = this.querySelector('[data-slider-current]');
    if (counter) counter.textContent = String(index + 1);
  }
}

class NutrimaTabs extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
    this.panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tabpanel"]')));
    if (!this.tabs.length) return;

    const { signal } = this.#abort;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const rtl = getComputedStyle(this).direction === 'rtl';
          const last = this.tabs.length - 1;
          let next = -1;
          if (event.key === 'ArrowRight') next = rtl ? i - 1 : i + 1;
          else if (event.key === 'ArrowLeft') next = rtl ? i + 1 : i - 1;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = last;
          else return;
          event.preventDefault();
          if (next < 0) next = last;
          if (next > last) next = 0;
          this.select(next, true);
        },
        { signal }
      );
    });

    this.setAttribute('data-ready', '');
    this.select(0);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  /**
   * @param {number} index
   * @param {boolean} [focus]
   */
  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

class NutrimaCompare extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const range = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="range"]'));
    if (!range) return;
    const update = () => this.style.setProperty('--nutrima-compare-position', `${range.value}%`);
    range.addEventListener('input', update, { signal: this.#abort.signal });
    this.setAttribute('data-ready', '');
    update();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }
}

for (const [name, constructor] of /** @type {[string, CustomElementConstructor][]} */ ([
  ['nutrima-slider', NutrimaSlider],
  ['nutrima-tabs', NutrimaTabs],
  ['nutrima-compare', NutrimaCompare],
])) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
